import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import { getExaminationDetails } from '../../../../redux/features/crmSlice';
import ExamScoreChart from './ExamScoreChart';

/**
 * the test sequence pre -> exam -> post is ORDINAL , so it takes a one hue ramp
 * rather than arbitrary decorative colours. these are steps 250 / 400 / 550 of the
 * documented blue ramp , validated with the dataviz validator in --ordinal mode
 * against both MUI paper surfaces : monotone lightness , adjacent dL >= 0.06 ,
 * light end clears 2:1 , single hue . the anchor flips in dark mode.
 * an 8 step version was tried first and FAILED the adjacent dL gate , which is why
 * the ramp encodes the three categories rather than every individual test.
 */
const TIERS = {
  light: ['#86b6ef', '#3987e5', '#1c5cab'],
  dark: ['#1c5cab', '#3987e5', '#86b6ef'],
};

const tierOf = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('pre')) return 0;
  if (n.includes('post')) return 2;
  return 1;
};

/**
 * decorative gradients for the timeline cards , keyed by position in the sequence.
 * these are CHROME , not a data encoding : the chart keeps its own validated series
 * palette and carries the legend. blue and orange are deliberately absent here so a
 * card can never be mistaken for the chart's "previous" / "current" series colours.
 * white text clears 4.5:1 against the lightest end of every gradient
 * ( worst 5.02:1 on emerald , checked with the contrast helper ).
 */
const CARD_GRADIENTS = [
  { from: '#3730a3', to: '#1e1b4b' }, // indigo
  { from: '#0f766e', to: '#042f2e' }, // teal
  { from: '#9f1239', to: '#4c0519' }, // rose
  { from: '#15803d', to: '#052e16' }, // emerald
  { from: '#6b21a8', to: '#2e1065' }, // violet
  { from: '#92400e', to: '#451a03' }, // amber
];

//ON A DARK CARD THE STATUS TINTS HAVE TO LIGHTEN OR THEY DISAPPEAR
const ON_DARK = { up: '#4ade80', down: '#fca5a5' };

const TIER_ICON = ['mdi:flag-outline', 'mdi:notebook-edit-outline', 'mdi:trophy-outline'];

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');
const marksText = (o, t) => (o === null || o === undefined ? '—' : `${o} / ${t ?? '—'}`);
const scorePercent = (test) =>
  test?.marks_obtained === null || test?.marks_obtained === undefined || !test?.total_marks
    ? null
    : (test.marks_obtained / test.total_marks) * 100;

const RATING_FIELDS = [
  { key: 'handwriting_rating', label: 'Handwriting', icon: 'mdi:pencil-outline' },
  { key: 'student_engagement_rating', label: 'Engagement', icon: 'mdi:hand-wave-outline' },
  { key: 'extra_curricular_activities_rating', label: 'Extra curricular', icon: 'mdi:star-outline' },
  { key: 'attendance_rating', label: 'Attendance', icon: 'mdi:calendar-check-outline' },
  { key: 'punctuality_rating', label: 'Punctuality', icon: 'mdi:clock-outline' },
];

const Delta = ({ delta, bold = false, onDark = false }) => {
  const theme = useTheme();
  if (delta === null || delta === undefined) return null;
  const up = delta >= 0;
  const color = onDark
    ? up
      ? ON_DARK.up
      : ON_DARK.down
    : up
      ? theme.palette.success.main
      : theme.palette.error.main;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      {/* ARROW + NUMBER , SO DIRECTION IS NEVER CARRIED BY COLOUR ALONE */}
      <Icon icon={up ? 'mdi:trending-up' : 'mdi:trending-down'} width={bold ? 18 : 14} height={bold ? 18 : 14} color={color} />
      <Typography variant="caption" sx={{ fontWeight: 700, color, fontSize: bold ? 13 : 12 }}>
        {up ? '+' : ''}
        {delta.toFixed(0)}%
      </Typography>
    </Stack>
  );
};

/**
 * section heading with an accent badge , used above each block of the content pane
 * @author Akhil E M
 * @createdAt 05-08-2026
 * @updates 05-08-2026(Akhil EM),
 * @type component
 */
const SectionHead = ({ icon, title, subtitle, gradient }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        color: '#fff',
        background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
        boxShadow: (theme) => `0 3px 10px ${alpha(gradient.from, theme.palette.mode === 'dark' ? 0.5 : 0.3)}`,
      }}
    >
      <Icon icon={icon} width="18" height="18" />
    </Box>
    <Stack spacing={0.15} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
        {title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {subtitle}
      </Typography>
    </Stack>
  </Stack>
);

/**
 * one criterion as a labelled meter , the readable twin of the chart
 * @author Akhil E M
 * @createdAt 05-08-2026
 * @updates 05-08-2026(Akhil EM),
 * @type component
 */
/**
 * one criterion as a meter, with its movement against the SAME criterion in the previous
 * test called out explicitly. totals differ between tests, so the delta is in percentage
 * points, never in raw marks.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type component
 */
//DIFFICULTY BAND OF A CRITERION . ORDINAL ( basic -> advanced ) , SO IT TAKES A ONE HUE
//RAMP . A DELIBERATELY DIFFERENT HUE FROM THE BLUE TEST RAMP ABOVE , SO A BAND CANNOT BE
//MISREAD AS A TEST. THE BACKEND RESOLVES THE NAME , INCLUDING "Category not added" FOR
//THE ROWS WHERE THE COLUMN IS STILL NULL.
const CATEGORY_TINT = { 1: '#7e9cc4', 2: '#4a6fa5', 3: '#1f3f6b' };

/**
 * the difficulty band chip for one criterion. renders muted when the criterion has no
 * category set, rather than hiding the fact.
 * @author Akhil E M
 * @createdAt 19-08-2026
 * @updates 19-08-2026(Akhil EM),
 * @type component
 */
const CategoryChip = ({ categoryId, categoryName }) => {
  const theme = useTheme();
  if (!categoryName) return null;
  const tint = CATEGORY_TINT[categoryId];
  return (
    <Chip
      size="small"
      label={categoryName}
      sx={{
        height: 18,
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.85 },
        ...(tint
          ? { color: tint, bgcolor: alpha(tint, 0.12), border: '1px solid', borderColor: alpha(tint, 0.35) }
          : {
              color: 'text.disabled',
              bgcolor: 'transparent',
              border: '1px dashed',
              borderColor: alpha(theme.palette.text.primary, 0.25),
              fontWeight: 500,
            }),
      }}
    />
  );
};

const CriterionRow = ({ name, obtained, total, previous, categoryId, categoryName, gradient, sub = false }) => {
  const theme = useTheme();
  const pct = obtained === null || obtained === undefined || !total ? null : (obtained / total) * 100;
  const prevPct =
    previous && previous.marks_obtained !== null && previous.marks_obtained !== undefined && previous.total_marks
      ? (previous.marks_obtained / previous.total_marks) * 100
      : null;
  const delta = pct !== null && prevPct !== null ? pct - prevPct : null;

  const moved = delta !== null && Math.abs(delta) >= 0.5;
  const up = delta !== null && delta > 0;
  const tint = !moved ? null : up ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Box
      sx={{
        //PADDING IS IDENTICAL WHETHER OR NOT THE ROW MOVED , SO TINTING NEVER SHIFTS A ROW.
        //SEPARATE pl / pr / ml KEYS , NEVER THE px SHORTHAND , WHICH WOULD OVERRIDE pl.
        py: 1,
        pl: sub ? 4 : 1,
        pr: 1,
        ml: -1,
        borderRadius: 1.5,
        //A CHANGED CRITERION IS TINTED SO IT IS FINDABLE WITHOUT READING EVERY NUMBER
        bgcolor: moved ? alpha(tint, 0.07) : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 0.75 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {!sub && (
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{ fontWeight: sub ? 400 : 600, color: sub ? 'text.secondary' : 'text.primary' }}
            noWrap
          >
            {name ?? '—'}
          </Typography>
          <CategoryChip categoryId={categoryId} categoryName={categoryName} />
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
          {delta !== null &&
            (moved ? (
              //ARROW PLUS NUMBER , NEVER COLOUR ALONE
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Icon icon={up ? 'mdi:arrow-up-bold' : 'mdi:arrow-down-bold'} width="14" height="14" color={tint} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: tint }}>
                  {up ? '+' : ''}
                  {delta.toFixed(0)}%
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Icon icon="mdi:minus" width="14" height="14" color={theme.palette.text.disabled} />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  no change
                </Typography>
              </Stack>
            ))}
          <Typography
            variant="body2"
            sx={{ fontWeight: sub ? 500 : 700, fontVariantNumeric: 'tabular-nums' }}
          >
            {obtained === null || obtained === undefined ? '—' : `${obtained} / ${total ?? '—'}`}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          height: sub ? 4 : 6,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.text.primary, 0.07),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${pct ?? 0}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`,
            opacity: sub ? 0.65 : 1,
            transition: 'width 400ms ease',
          }}
        />
        {/* WHERE THIS CRITERION SAT IN THE PREVIOUS TEST . THE TICK FLIPS CONTRAST
            DEPENDING ON WHETHER IT LANDS ON THE FILLED BAR OR ON THE EMPTY TRACK */}
        {prevPct !== null && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `calc(${prevPct}% - 1px)`,
              width: 2,
              bgcolor: prevPct <= (pct ?? 0) ? alpha('#fff', 0.9) : alpha(theme.palette.text.primary, 0.55),
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const ScoreRing = ({ percent, color, size = 96, textColor }) => {
  const theme = useTheme();
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = percent === null ? 0 : (percent / 100) * circumference;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.16)} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 450ms ease' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, color: textColor || theme.palette.text.primary }}>
          {percent === null ? '—' : `${percent.toFixed(0)}%`}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * fullscreen test detail popup for one subject enrollment of the cro dashboard.
 * the left rail is a timeline of every test , the right pane shows the selected
 * test's analytics. the first test is charted alone , every later test against the
 * one before it , all derived on the client from the payload already fetched.
 * @author Akhil E M
 * @createdAt 05-08-2026
 * @updates 05-08-2026(Akhil EM),
 * @type component
 * @packages @mui/material,@iconify/react,dayjs
 */
const TestDetailView = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const tierColors = theme.palette.mode === 'dark' ? TIERS.dark : TIERS.light;

  const detailRef = useRef(null);
  //md AND UP IS THE TWO PANE LAYOUT WHERE THE DETAIL SCROLLS ITSELF ,
  //BELOW THAT EVERYTHING STACKS AND THE DIALOG IS THE SCROLLER
  const isTwoPane = useMediaQuery(theme.breakpoints.up('md'));

  /**
   * select a test and bring its detail into view.
   * the two layouts scroll different elements , so the target differs :
   * two pane -> the detail pane scrolls itself back to the top
   * stacked  -> the detail block is scrolled into the dialog's viewport
   * @author Akhil E M
   * @createdAt 05-08-2026
   * @updates 05-08-2026(Akhil EM),
   * @type function
   * @param {number} id student_examination_id
   */
  const handleSelectTest = useCallback(
    (id) => {
      setSelectedId(id);

      //LET THE NEW CONTENT COMMIT BEFORE MEASURING OR SCROLLING
      requestAnimationFrame(() => {
        const node = detailRef.current;
        if (!node) return;

        const reduceMotion =
          typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = reduceMotion ? 'auto' : 'smooth';

        //scrollHeight > clientHeight MEANS THE PANE OWNS ITS SCROLLING
        if (isTwoPane && node.scrollHeight > node.clientHeight) {
          node.scrollTo({ top: 0, behavior });
          return;
        }
        node.scrollIntoView({ behavior, block: 'start' });
      });
    },
    [isTwoPane],
  );

  const examinationDetails = useSelector((state) => state.crm.examinationDetails);
  const loading = useSelector((state) => state.crm.examinationDetailsLoading);

  useEffect(() => {
    if (open && row?.subject_enrollment_id) {
      dispatch(getExaminationDetails({ subject_enrollment_id: row.subject_enrollment_id }));
    }
  }, [open, row?.subject_enrollment_id, dispatch]);

  useEffect(() => {
    if (examinationDetails?.length) setSelectedId(examinationDetails[0].student_examination_id);
  }, [examinationDetails]);

  const tests = useMemo(
    () =>
      (examinationDetails || []).map((test, index, all) => {
        const previous = index > 0 ? all[index - 1] : null;
        const currentPct = scorePercent(test);
        const previousPct = scorePercent(previous);
        const tier = tierOf(test.student_examination_type_name);
        return {
          ...test,
          index,
          previous,
          tier,
          color: tierColors[tier],
          gradient: CARD_GRADIENTS[index % CARD_GRADIENTS.length],
          percent: currentPct,
          delta: currentPct !== null && previousPct !== null ? currentPct - previousPct : null,
        };
      }),
    [examinationDetails, tierColors],
  );

  /**
   * pre assessment vs the last completed test.
   * deliberately hidden when only two tests exist ( pre + exam 1 ) : the exam 1 panel
   * already charts exactly that pair , so the card would only repeat it. from three
   * tests onwards the pre -> latest span is information the per test charts never show.
   */
  const overall = useMemo(() => {
    if (tests.length < 3) return null;

    const from = tests[0];
    const to = tests[tests.length - 1];
    //ONLY MEANINGFUL WHEN THE SPAN ACTUALLY STARTS AT THE PRE ASSESSMENT
    if (from.tier !== 0) return null;
    if (from.percent === null || to.percent === null) return null;

    let improved = 0;
    let comparable = 0;
    if (from.hasBreakdown && to.hasBreakdown) {
      const flatten = (test) => {
        const map = new Map();
        (test.criteria || []).forEach((parent) => {
          const add = (row) => {
            if (row.marks_obtained === null || row.marks_obtained === undefined || !row.total_marks) return;
            map.set(row.student_examination_criteria_id, (row.marks_obtained / row.total_marks) * 100);
          };
          add(parent);
          (parent.subCriteria || []).forEach(add);
        });
        return map;
      };
      const start = flatten(from);
      const end = flatten(to);
      end.forEach((value, key) => {
        if (!start.has(key)) return;
        comparable += 1;
        if (value > start.get(key)) improved += 1;
      });
    }

    return { from, to, delta: to.percent - from.percent, improved, comparable };
  }, [tests]);

  const selected = tests.find((t) => t.student_examination_id === selectedId) || tests[0] || null;

  //FLATTEN THE PREVIOUS TEST'S CRITERIA ( PARENTS AND SUB CRITERIA ALIKE ) SO EACH ROW CAN
  //LOOK UP ITS OWN EARLIER SCORE BY student_examination_criteria_id
  const previousCriteria = useMemo(() => {
    const map = new Map();
    (selected?.previous?.criteria || []).forEach((p) => {
      map.set(p.student_examination_criteria_id, p);
      (p.subCriteria || []).forEach((s) => map.set(s.student_examination_criteria_id, s));
    });
    return map;
  }, [selected]);

  const ratings = selected
    ? RATING_FIELDS.filter((r) => selected[r.key] !== null && selected[r.key] !== undefined && selected[r.key] !== '')
    : [];

  //SLIM STYLED SCROLLBAR , APPLIED TO EACH PANE THAT SCROLLS ON ITS OWN
  const thumb = alpha(theme.palette.text.primary, 0.18);
  const SCROLLBAR = {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} transparent`,
    '&::-webkit-scrollbar': { width: 6, height: 6 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: thumb, borderRadius: 999 },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.32) },
  };

  //THE TWO PANES OWN THEIR SCROLLING SO THE RAIL STAYS PUT WHILE THE DETAIL MOVES.
  //ON XS EVERYTHING FALLS BACK TO NORMAL PAGE FLOW.
  const PANE_HEIGHT = { xs: 'auto', md: 'calc(100vh - 168px)' };

  return (
    <GlobalDialog
      fullScreen
      open={open}
      setOpen={setOpen}
      closeButton
      title={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              background: `linear-gradient(135deg, ${tierColors[1]}, ${tierColors[2]})`,
            }}
          >
            <Icon icon="mdi:chart-box-outline" width="21" height="21" />
          </Box>
          <Stack spacing={0.1}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Test Details
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {[row?.student_name, row?.subject_name, row?.class_name && `Class ${row.class_name}`]
                .filter(Boolean)
                .join('  ·  ')}
            </Typography>
          </Stack>
        </Stack>
      }
      popupButton={
        <Button
          size="small"
          startIcon={<Icon icon="mdi:chart-timeline-variant" width="18" height="18" />}
          onClick={() => setOpen(true)}
        >
          View
        </Button>
      }
      content={
        loading ? (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ py: 2 }}>
            <Stack spacing={1.5} sx={{ width: { xs: '100%', md: 320 } }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={78} />
              ))}
            </Stack>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="rounded" height={460} />
            </Box>
          </Stack>
        ) : !tests.length ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Icon icon="mdi:clipboard-text-off-outline" width="46" height="46" color={theme.palette.text.disabled} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              No examinations recorded for this subject enrollment
            </Typography>
          </Box>
        ) : (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{ height: PANE_HEIGHT, overflow: { xs: 'visible', md: 'hidden' } }}
          >
            {/* ================= LEFT : TEST TIMELINE ( STICKY PANE ) ================= */}
            <Box
              sx={{
                width: { xs: '100%', md: 330 },
                flexShrink: 0,
                height: PANE_HEIGHT,
                overflowY: { xs: 'visible', md: 'auto' },
                pr: { md: 1.25 },
                pt: 0.5,
                ...SCROLLBAR,
              }}
            >
              {/* ---------- OVERALL PROGRESS : PRE ASSESSMENT -> LAST COMPLETED TEST ---------- */}
              {overall && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2.5,
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                    //THE GRADIENT SPANS THE FIRST TEST'S COLOUR TO THE LAST ONE'S ,
                    //SO THE CARD ITSELF DRAWS THE JOURNEY IT DESCRIBES
                    background: `linear-gradient(135deg, ${overall.from.gradient.from} 0%, ${overall.to.gradient.to} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(overall.from.gradient.from, 0.4)}`,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -40,
                      right: -30,
                      width: 130,
                      height: 130,
                      borderRadius: '50%',
                      bgcolor: alpha('#fff', 0.08),
                    }}
                  />

                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                    <Icon icon="mdi:trending-up" width="16" height="16" color="#fff" />
                    <Typography variant="overline" sx={{ color: alpha('#fff', 0.9), letterSpacing: 1, lineHeight: 1 }}>
                      Overall progress
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={1}>
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }} noWrap>
                        {overall.from.student_examination_type_name}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>
                        {overall.from.percent.toFixed(0)}%
                      </Typography>
                    </Stack>

                    <Icon icon="mdi:arrow-right" width="20" height="20" color={alpha('#fff', 0.7)} />

                    <Stack sx={{ minWidth: 0, textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }} noWrap>
                        {overall.to.student_examination_type_name}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>
                        {overall.to.percent.toFixed(0)}%
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* THE SPAN AS A METER : START MARKED , GROWTH FILLED TO THE CURRENT LEVEL */}
                  <Box sx={{ mt: 1.75, height: 6, borderRadius: 999, bgcolor: alpha('#fff', 0.22), position: 'relative' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: `${Math.max(overall.from.percent, overall.to.percent)}%`,
                        borderRadius: 999,
                        bgcolor: '#fff',
                        transition: 'width 400ms ease',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -3,
                        left: `calc(${overall.from.percent}% - 1px)`,
                        width: 2,
                        height: 12,
                        bgcolor: alpha('#000', 0.35),
                        borderRadius: 1,
                      }}
                    />
                  </Box>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Icon
                        icon={overall.delta >= 0 ? 'mdi:arrow-up-bold' : 'mdi:arrow-down-bold'}
                        width="16"
                        height="16"
                        color={overall.delta >= 0 ? ON_DARK.up : ON_DARK.down}
                      />
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 15, color: overall.delta >= 0 ? ON_DARK.up : ON_DARK.down }}
                      >
                        {overall.delta >= 0 ? '+' : ''}
                        {overall.delta.toFixed(0)}%
                      </Typography>
                    </Stack>

                    {overall.comparable > 0 && (
                      <Typography variant="caption" sx={{ color: alpha('#fff', 0.8) }}>
                        {overall.improved} of {overall.comparable} criteria improved
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}

              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 1.5 }}
              >
                Test timeline
              </Typography>

              <Stack>
                {tests.map((test, i) => {
                  const active = test.student_examination_id === selected?.student_examination_id;
                  const last = i === tests.length - 1;
                  return (
                    // PLAIN FLEX BOX , NOT A Stack : Stack's spacing rule injects
                    // "& > :not(style) + :not(style) { margin: 0 }" which out-specifies the
                    // card's own sx and silently wipes its bottom margin
                    <Box
                      key={test.student_examination_id}
                      sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}
                    >
                      {/* SPINE : THE TESTS ARE A SEQUENCE , SO DRAW THEM AS ONE .
                          THE COLUMN IS WIDER THAN THE 24px NODE SO THE ACTIVE RING HAS ROOM
                          AND IS NOT CLIPPED BY THE RAIL'S OVERFLOW */}
                      <Stack alignItems="center" sx={{ width: 34, flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            //ALIGNS THE NODE WITH THE CARD'S FIRST TEXT LINE
                            mt: '13px',
                            display: 'grid',
                            placeItems: 'center',
                            color: '#fff',
                            background: `linear-gradient(135deg, ${test.gradient.from}, ${test.gradient.to})`,
                            boxShadow: active
                              ? `0 0 0 4px ${alpha(test.gradient.from, 0.28)}`
                              : `0 1px 3px ${alpha('#000', 0.25)}`,
                            transition: 'box-shadow 180ms ease',
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon={TIER_ICON[test.tier]} width="13" height="13" />
                        </Box>
                        {!last && <Box sx={{ flex: 1, width: '2px', bgcolor: 'divider', mt: 1, mb: 0 }} />}
                      </Stack>

                      {/* CARD */}
                      <Paper
                        elevation={0}
                        onClick={() => handleSelectTest(test.student_examination_id)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          //GAP BETWEEN CARDS , NONE AFTER THE LAST . IT SITS ON THE CARD ( NOT THE ROW )
                          //SO THE SPINE , WHICH STRETCHES TO THE ROW'S CONTENT BOX , RUNS THROUGH IT
                          mb: last ? 0 : 3,
                          //ALIGN-SELF START STOPS THE CARD BEING STRETCHED OVER ITS OWN MARGIN
                          alignSelf: 'flex-start',
                          p: 1.75,
                          cursor: 'pointer',
                          borderRadius: 2.5,
                          position: 'relative',
                          overflow: 'hidden',
                          color: '#fff',
                          background: `linear-gradient(135deg, ${test.gradient.from} 0%, ${test.gradient.to} 100%)`,
                          border: '1px solid',
                          borderColor: active ? alpha('#fff', 0.5) : 'transparent',
                          boxShadow: active
                            ? `0 8px 22px ${alpha(test.gradient.from, 0.45)}`
                            : `0 2px 8px ${alpha('#000', 0.18)}`,
                          transform: active ? 'translateX(2px)' : 'none',
                          opacity: active ? 1 : 0.82,
                          transition: 'all 200ms ease',
                          '&:hover': { opacity: 1, boxShadow: `0 6px 18px ${alpha(test.gradient.from, 0.4)}` },
                        }}
                      >
                        {/* SOFT HIGHLIGHT SO THE GRADIENT READS AS A SURFACE , NOT A FLAT BLOCK */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -34,
                            right: -22,
                            width: 92,
                            height: 92,
                            borderRadius: '50%',
                            bgcolor: alpha('#fff', 0.09),
                            pointerEvents: 'none',
                          }}
                        />

                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff' }} noWrap>
                            {test.student_examination_type_name}
                          </Typography>
                          <Delta delta={test.delta} onDark />
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }}>
                            {formatDate(test.examination_date)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}
                          >
                            {marksText(test.marks_obtained, test.total_marks)}
                          </Typography>
                        </Stack>

                        {/* MINI METER ON THE DARK SURFACE */}
                        <Box sx={{ mt: 1.25, height: 5, borderRadius: 999, bgcolor: alpha('#fff', 0.22) }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: `${test.percent ?? 0}%`,
                              borderRadius: 999,
                              bgcolor: '#fff',
                              transition: 'width 350ms ease',
                            }}
                          />
                        </Box>

                        {!test.hasBreakdown && (
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), display: 'block', mt: 0.75 }}>
                            no criteria data
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {/* ================= RIGHT : SELECTED TEST ( SCROLLS ON ITS OWN ) ================= */}
            <Box
              ref={detailRef}
              //SCROLL ANCHOR FOR THE STACKED LAYOUT , CLEARS THE STICKY DIALOG HEADER
              sx={{
                flex: 1,
                minWidth: 0,
                height: PANE_HEIGHT,
                overflowY: { xs: 'visible', md: 'auto' },
                pr: { md: 1 },
                pt: 0.5,
                pb: 2,
                scrollMarginTop: 16,
                ...SCROLLBAR,
              }}
            >
              {selected && (
                <Stack spacing={3}>
                  {/* HERO */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, md: 3.5 },
                      borderRadius: 3,
                      position: 'relative',
                      overflow: 'hidden',
                      color: '#fff',
                      //FULL GRADIENT , MATCHING THE SELECTED TIMELINE CARD SO THE TWO HALVES READ AS ONE
                      background: `linear-gradient(135deg, ${selected.gradient.from} 0%, ${selected.gradient.to} 100%)`,
                      boxShadow: `0 10px 30px ${alpha(selected.gradient.from, 0.35)}`,
                      //TWO SOFT ORBS KEEP THE LARGE FILL FROM READING AS A FLAT SLAB
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: -70,
                        right: -40,
                        width: 220,
                        height: 220,
                        borderRadius: '50%',
                        background: alpha('#fff', 0.08),
                      },
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -90,
                        right: 90,
                        width: 170,
                        height: 170,
                        borderRadius: '50%',
                        background: alpha('#fff', 0.05),
                      },
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={3}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <ScoreRing percent={selected.percent} color="#ffffff" textColor="#ffffff" />

                      <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                            {selected.student_examination_type_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={marksText(selected.marks_obtained, selected.total_marks)}
                            sx={{
                              fontWeight: 700,
                              color: '#fff',
                              bgcolor: alpha('#fff', 0.2),
                              border: '1px solid',
                              borderColor: alpha('#fff', 0.35),
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          />
                        </Stack>

                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Icon icon="mdi:calendar-blank-outline" width="15" height="15" color={alpha('#fff', 0.8)} />
                            <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                              {formatDate(selected.examination_date)}
                            </Typography>
                          </Stack>
                          {selected.delta !== null && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Delta delta={selected.delta} bold onDark />
                              <Typography variant="body2" sx={{ color: alpha('#fff', 0.85) }}>
                                vs {selected.previous?.student_examination_type_name}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>

                        {ratings.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                            {ratings.map((r) => (
                              <Chip
                                key={r.key}
                                size="small"
                                icon={<Icon icon={r.icon} width="14" height="14" color="#fff" />}
                                label={`${r.label} ${selected[r.key]}`}
                                sx={{
                                  color: '#fff',
                                  bgcolor: alpha('#fff', 0.12),
                                  border: '1px solid',
                                  borderColor: alpha('#fff', 0.28),
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </Stack>

                    {selected.tutor_suggestions ? (
                      <Box
                        sx={{
                          mt: 2.5,
                          pt: 2,
                          position: 'relative',
                          borderTop: '1px solid',
                          borderColor: alpha('#fff', 0.22),
                        }}
                      >
                        <Typography variant="caption" sx={{ color: alpha('#fff', 0.75) }}>
                          Tutor suggestions
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {selected.tutor_suggestions}
                        </Typography>
                      </Box>
                    ) : null}
                  </Paper>

                  {/* ANALYTICS */}
                  <Box>
                    <SectionHead
                      gradient={selected.gradient}
                      icon={selected.previous?.hasBreakdown ? 'mdi:compare-horizontal' : 'mdi:chart-bar'}
                      title={
                        selected.previous?.hasBreakdown
                          ? `${selected.previous.student_examination_type_name} vs ${selected.student_examination_type_name}`
                          : 'Score by criteria'
                      }
                      subtitle={
                        selected.previous?.hasBreakdown
                          ? 'Each criterion as a percentage of its total, against the previous test'
                          : 'Each criterion as a percentage of its total'
                      }
                    />

                    <Box>
                      {selected.hasBreakdown || selected.previous?.hasBreakdown ? (
                        <ExamScoreChart
                          currentTest={selected}
                          previousTest={selected.previous}
                          currentLabel={selected.student_examination_type_name}
                          previousLabel={selected.previous?.student_examination_type_name}
                        />
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            py: 6,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <Icon icon="mdi:chart-box-outline" width="34" height="34" color={theme.palette.text.disabled} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            No criteria breakdown recorded for this test
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Box>

                  {/* THE READABLE TWIN OF THE CHART : EVERY VALUE ALSO AVAILABLE AS TEXT */}
                  {selected.hasBreakdown && (
                    <Box>
                      <SectionHead
                        gradient={selected.gradient}
                        icon="mdi:format-list-checks"
                        title="Criteria detail"
                        subtitle={
                          previousCriteria.size
                            ? `Every criterion scored in this test, with its movement against ${selected.previous?.student_examination_type_name}`
                            : 'Every criterion and sub-criterion scored in this test'
                        }
                      />

                      <Stack spacing={1.5}>
                        {selected.criteria.map((parent) => (
                          <Paper
                            key={parent.student_examination_criteria_id}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              overflow: 'hidden',
                              '&:before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                background: `linear-gradient(180deg, ${selected.gradient.from}, ${selected.gradient.to})`,
                              },
                            }}
                          >
                            <Box sx={{ pl: 1 }}>
                              <CriterionRow
                                name={parent.criteria_name}
                                obtained={parent.marks_obtained}
                                total={parent.total_marks}
                                previous={previousCriteria.get(parent.student_examination_criteria_id)}
                                categoryId={parent.category_id}
                                categoryName={parent.category_name}
                                gradient={selected.gradient}
                              />
                              {parent.subCriteria?.map((sub) => (
                                <CriterionRow
                                  key={sub.student_examination_child_id}
                                  name={sub.criteria_name}
                                  obtained={sub.marks_obtained}
                                  total={sub.total_marks}
                                  previous={previousCriteria.get(sub.student_examination_criteria_id)}
                                  categoryId={sub.category_id}
                                  categoryName={sub.category_name}
                                  gradient={selected.gradient}
                                  sub
                                />
                              ))}
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
        )
      }
    />
  );
};

export default TestDetailView;
